import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { EmojiPickerButton, EmojiReactions } from '@/components/EmojiPicker';
import { FileSelector, AttachmentsList } from '@/components/FileUpload';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Edit, Trash2, Plus, Mail, Send, CheckCircle, User, Folder, FolderPlus, MoveRight, Flag, AlertCircle, Check, ChevronDown, ChevronRight, Palette, Settings, BarChart3, Wrench, ArrowUpDown } from 'lucide-react';
import TabOrderDialog from '@/components/admin/TabOrderDialog';
import { arrangeTabs } from './admin/adminTabs';
import AdminAccountAccess from './admin/AdminAccountAccess';
import AdminMembershipManagement from './AdminMembershipManagement';
import AdminLegalDocuments from './AdminLegalDocuments';
import AdminPricingSettings from './AdminPricingSettings';
import AdminTierPricing from './AdminTierPricing';
import AdminSubscriptionPricing from './AdminSubscriptionPricing';
import AdminAnalytics from './AdminAnalytics';
import AdminEventCleanup from './AdminEventCleanup';
import DmReportDetail from '@/components/admin/DmReportDetail';
import MentionAutocomplete from '@/components/MentionAutocomplete';
import MentionText from '@/components/MentionText';
import { formatTimeTo12Hour } from '@/utils/timezoneUtils';

// Import refactored tab components
import AdminModulesTab from './admin/tabs/AdminModulesTab';
import AdminFoldersTab from './admin/tabs/AdminFoldersTab';
import AdminPacingTab from './admin/tabs/AdminPacingTab';
import AdminPractitionersTab from './admin/tabs/AdminPractitionersTab';
import AdminVendorInvitesTab from './admin/tabs/AdminVendorInvitesTab';
import AdminOnboardingTab from './admin/tabs/AdminOnboardingTab';
import AdminContentTab from './admin/tabs/AdminContentTab';
import AdminForumTab from './admin/tabs/AdminForumTab';
import AdminCalendarTab from './admin/tabs/AdminCalendarTab';
import AdminEventApprovalsTab from './admin/tabs/AdminEventApprovalsTab';
import AdminGuidesTab from './admin/tabs/AdminGuidesTab';
import AdminStreamsTab from './admin/tabs/AdminStreamsTab';
import AdminPromotionsTab from './admin/tabs/AdminPromotionsTab';
import AdminMusicTab from './admin/tabs/AdminMusicTab';
import AdminPortalVideoTab from './admin/tabs/AdminPortalVideoTab';
import AdminMessagesTab from './admin/tabs/AdminMessagesTab';
import AdminPendingTab from './admin/tabs/AdminPendingTab';
import AdminReportsTab from './admin/tabs/AdminReportsTab';
import AdminContributorsTab from './admin/tabs/AdminContributorsTab';
import AdminWorkshopsTab from './admin/tabs/AdminWorkshopsTab';
import AdminWorkshopAnalyticsTab from './admin/tabs/AdminWorkshopAnalyticsTab';
import AdminPayoutsTab from './admin/tabs/AdminPayoutsTab';
import AdminTaxTab from './admin/tabs/AdminTaxTab';
import AdminAssessmentScoringTab from './admin/tabs/AdminAssessmentScoringTab';
import AdminAssessmentBuilderTab from './admin/tabs/AdminAssessmentBuilderTab';
import AdminLaunchTab from './admin/tabs/AdminLaunchTab';
import AdminStripeTab from './admin/tabs/AdminStripeTab';
import AdminHelpVideosTab from './admin/tabs/AdminHelpVideosTab';
import AdminSpotlightsTab from './admin/tabs/AdminSpotlightsTab';
import AdminShelvesTab from './admin/tabs/AdminShelvesTab';
import AdminInventoryTab from './admin/tabs/AdminInventoryTab';
import AdminQATab from './admin/tabs/AdminQATab';
import AdminWorksheetsTab from './admin/tabs/AdminWorksheetsTab';
import AdminRitualCardsTab from './admin/tabs/AdminRitualCardsTab';
import AdminEmailsTab from './admin/tabs/AdminEmailsTab';
import AdminMarketplaceOrdersTab from './admin/tabs/AdminMarketplaceOrdersTab';
import AdminHeldPayoutsTab from './admin/tabs/AdminHeldPayoutsTab';
import AdminBlockedMembersTab from './admin/tabs/AdminBlockedMembersTab';

// Marketplace Admin Components
import MarketplaceAdminDashboard from './marketplace/AdminDashboard';
import MarketplaceVendorApprovals from './marketplace/VendorApprovals';

// Attention Indicator Component - Red badge for tabs needing admin attention
const AttentionBadge = ({ count }) => {
  if (!count || count <= 0) return null;
  return (
    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 animate-pulse">
      {count > 99 ? '99+' : count}
    </span>
  );
};

// Tab Trigger with optional attention indicator
const AdminTabTrigger = ({ value, label, attentionCount, className = '' }) => (
  <TabsTrigger 
    value={value} 
    className={`relative flex-shrink-0 whitespace-nowrap px-3 py-2 text-sm ${className}`}
    data-testid={`admin-tab-${value}`}
  >
    {label}
    <AttentionBadge count={attentionCount} />
  </TabsTrigger>
);

// Reports Manager Component
const ReportsManager = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const resolveReport = async (reportId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/reports/${reportId}/resolve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report marked as resolved');
      fetchReports();
    } catch (error) {
      toast.error('Failed to resolve report');
    }
  };

  const deleteReportedPost = async (postId, reportId) => {
    if (!window.confirm('Are you sure you want to delete this reported post and all its replies?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/forum/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted successfully');
      // Also mark report as resolved
      await resolveReport(reportId);
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const getReasonBadgeColor = (reason) => {
    const colors = {
      harassment: 'bg-red-100 text-red-800',
      spam: 'bg-orange-100 text-orange-800',
      inappropriate: 'bg-purple-100 text-purple-800',
      hate_speech: 'bg-red-200 text-red-900',
      violence: 'bg-red-300 text-red-900',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[reason] || colors.other;
  };

  if (loading) return <div className="text-center py-8">Loading reports...</div>;

  const pendingReports = reports.filter(r => r.status === 'pending');
  const resolvedReports = reports.filter(r => r.status === 'resolved');

  return (
    <div className="space-y-6">
      {/* Pending Reports */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-orange-500" />
          Pending Reports ({pendingReports.length})
        </h3>
        {pendingReports.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No pending reports</p>
        ) : (
          <div className="space-y-4">
            {pendingReports.map((report) => (
              <Card key={report.id} className="border-l-4 border-l-orange-500">
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getReasonBadgeColor(report.reason)}>
                            {report.reason.replace('_', ' ').toUpperCase()}
                          </Badge>
                          <span className="text-sm text-gray-500">
                            Reported by {report.reported_by_username}
                          </span>
                          <span className="text-sm text-gray-400">
                            {new Date(report.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        {report.description && (
                          <p className="text-sm text-gray-700 mb-3 italic">&quot;{report.description}&quot;</p>
                        )}
                        {report.type === 'direct_message' ? (
                          <DmReportDetail report={report} />
                        ) : (
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <Flag className="w-4 h-4 text-gray-500" />
                              <span className="font-semibold text-sm">Reported Post</span>
                              <span className="text-xs text-gray-500">by {report.post_author}</span>
                            </div>
                            <p className="font-medium text-sm mb-1">{report.post_title}</p>
                            <p className="text-sm text-gray-600 line-clamp-2">{report.post_content}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={() => resolveReport(report.id)}
                        variant="outline"
                        size="sm"
                        className="text-green-600 border-green-600 hover:bg-green-50"
                        data-testid={`report-resolve-${report.id}`}
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Mark Resolved
                      </Button>
                      {report.type !== 'direct_message' && (
                      <Button
                        onClick={() => deleteReportedPost(report.post_id, report.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Post
                      </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Resolved Reports */}
      {resolvedReports.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            Resolved Reports ({resolvedReports.length})
          </h3>
          <div className="space-y-4">
            {resolvedReports.map((report) => (
              <Card key={report.id} className="border-l-4 border-l-green-500 opacity-60">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge className={getReasonBadgeColor(report.reason)}>
                        {report.reason.replace('_', ' ').toUpperCase()}
                      </Badge>
                      <span className="text-sm text-gray-500 ml-2">
                        by {report.reported_by_username}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      Resolved {new Date(report.resolved_at).toLocaleDateString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Collapsible Category Header
const CategoryHeader = ({ label, icon: Icon, isExpanded, onToggle, color }) => (
  <button
    onClick={onToggle}
    className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-semibold tracking-wide transition-all duration-200 ${color} hover:brightness-110`}
    data-testid={`admin-category-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <Icon className="w-4 h-4 flex-shrink-0" />
    <span>{label}</span>
    {isExpanded ? <ChevronDown className="w-4 h-4 ml-auto" /> : <ChevronRight className="w-4 h-4 ml-auto" />}
  </button>
);

const AdminPanel = ({ user }) => {
  // Deep-linkable tab: /dashboard/admin?tab=pacing
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'modules';

  // Category expand/collapse state
  const [expandedCategories, setExpandedCategories] = useState({
    creation: true,
    management: true,
    backend: true,
    analytics: true
  });

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  // Tab arrangement, saved platform-wide. Empty object = the order the tabs were built in.
  const [tabOrder, setTabOrder] = useState({});
  const [tabOrderLoaded, setTabOrderLoaded] = useState(false);
  const [showTabOrder, setShowTabOrder] = useState(false);
  const arrangedCategories = arrangeTabs(tabOrder);

  useEffect(() => {
    axios.get(`${API}/admin/tab-order`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((r) => setTabOrder(r.data?.order || {}))
      .catch(() => setTabOrder({}))
      .finally(() => setTabOrderLoaded(true));
  }, []);

  // State for all content types
  const [modules, setModules] = useState([]);
  const [folders, setFolders] = useState([]);
  const [resources, setResources] = useState([]);
  const [contents, setContents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [messageFolders, setMessageFolders] = useState([]);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null); // For filtering messages by folder
  
  // Attention counts for tabs that need admin action
  const [attentionCounts, setAttentionCounts] = useState({
    pending: 0,
    reports: 0,
    workshops: 0,
    messages: 0,
    marketplace_vendors: 0,
    spotlight_promotions: 0,
    return_escalations: 0,
    event_approvals: 0
  });
  
  // State for forms
  const [editingModule, setEditingModule] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editingContent, setEditingContent] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingResource, setEditingResource] = useState(null);
  const [draggedResource, setDraggedResource] = useState(null);
  const [viewPhotoModal, setViewPhotoModal] = useState(false);
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState(null);
  const [selectedPhotoAttribution, setSelectedPhotoAttribution] = useState(null);

  // Refs for auto-scrolling to forms
  const moduleFormRef = useRef(null);
  const folderFormRef = useRef(null);
  const contentFormRef = useRef(null);
  const resourceFormRef = useRef(null);
  const streamFormRef = useRef(null);
  const musicFormRef = useRef(null);
  const categoryFormRef = useRef(null);
  const eventFormRef = useRef(null);
  const messageFolderFormRef = useRef(null);

  const [editingStream, setEditingStream] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedFolders, setExpandedFolders] = useState({});
  
  const [moduleForm, setModuleForm] = useState({
    title: '',
    description: '',
    order: 1,
    is_locked: true,
    required_previous_module: '',
    days_delay: 0
  });

  const [folderForm, setFolderForm] = useState({
    module_id: '',
    name: '',
    description: '',
    order: 1,
    days_delay: 0
  });

  const [contentForm, setContentForm] = useState({
    module_id: '',
    folder_id: '',
    title: '',
    content_type: 'blog',
    order: 1,
    generate_ai_image: false,
    content_data: {},
    is_public: false,
    is_listed: false,
    slug: '',
    unsplash_url: ''
  });
  
  const [photoOptions, setPhotoOptions] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [showUnsplashModal, setShowUnsplashModal] = useState(false);
  const [unsplashSearchQuery, setUnsplashSearchQuery] = useState('');
  const [unsplashResults, setUnsplashResults] = useState([]);

  // Background Music State
  const [backgroundMusic, setBackgroundMusic] = useState([]);
  const [pageAssignments, setPageAssignments] = useState({});
  const [moduleAssignments, setModuleAssignments] = useState({});
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const [musicTitle, setMusicTitle] = useState('');
  const [selectedMusicFile, setSelectedMusicFile] = useState(null);

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    allow_homework: false
  });

  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    timezone: 'America/Chicago',
    link: ''
  });

  const [resourceForm, setResourceForm] = useState({
    name: '',
    title: '',
    phone: '',
    bio: '',
    website: '',
    photo_url: '',
    facebook: '',
    instagram: '',
    linkedin: '',
    twitter: '',
    youtube: '',
    tiktok: '',
    spotify: '',
    email: '',
    custom_link_1_label: '',
    custom_link_1_url: '',
    custom_link_2_label: '',
    custom_link_2_url: '',
    custom_link_3_label: '',
    custom_link_3_url: ''
  });

  const [streamForm, setStreamForm] = useState({
    title: '',
    description: '',
    embed_url: '',
    scheduled_date: '',
    timezone: 'America/Chicago',
    is_live: false,
    add_to_calendar: false,
    // Paid stream fields
    is_paid: false,
    live_price: 0,
    replay_price: 0,
    stream_password: ''
  });

  const [messageForm, setMessageForm] = useState({
    recipient_ids: [],
    subject: '',
    message: '',
    priority: 'normal',
    send_notification: true
  });

  const [adminSelectedFiles, setAdminSelectedFiles] = useState([]);

  const [messageFolderForm, setMessageFolderForm] = useState({
    name: '',
    description: '',
    color: '#808080'
  });

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [editingMessageFolder, setEditingMessageFolder] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null); // Track which message is being replied to
  const [replyText, setReplyText] = useState(''); // Track reply text

  useEffect(() => {
    fetchAllData();
    fetchMessages();
    fetchUsers();
    fetchPendingUsers();
    fetchMessageFolders();
    fetchBackgroundMusic();
    fetchMusicAssignments();
    fetchAttentionCounts(); // Fetch counts for attention indicators
  }, []);

  // The badges used to sit at whatever they were when the page loaded, so approving
  // something left a number behind that was already dealt with. Three things refresh
  // them now: switching tab (above), coming back to the window, and — the one that
  // matters — any successful admin action, caught centrally so every tab benefits
  // without each of them having to remember to ask.
  useEffect(() => {
    let timer = null;
    const refresh = () => {
      clearTimeout(timer);
      timer = setTimeout(() => fetchAttentionCounts(), 400);
    };
    const interceptor = axios.interceptors.response.use((response) => {
      const method = (response.config?.method || '').toLowerCase();
      const url = response.config?.url || '';
      if (['post', 'put', 'patch', 'delete'].includes(method)
          && /\/(admin|workshops\/admin)\//.test(url)) {
        refresh();
      }
      return response;
    });
    window.addEventListener('focus', refresh);
    window.addEventListener('admin-data-changed', refresh);
    return () => {
      clearTimeout(timer);
      axios.interceptors.response.eject(interceptor);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('admin-data-changed', refresh);
    };
  }, []);
  
  // Fetch attention counts for tabs needing admin action
  const fetchAttentionCounts = async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [pendingRes, reportsRes, workshopsRes, mktVendorsRes, promosRes, escalationsRes, eventsRes, pollsRes] = await Promise.all([
        axios.get(`${API}/admin/users/pending?per_page=50`, { headers }).catch(() => ({ data: { users: [], total: 0 } })),
        axios.get(`${API}/admin/reports`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/workshops/admin/all`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/marketplace/vendors/pending?per_page=100`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/spotlight-promotions?status=paid_pending_approval`, { headers }).catch(() => ({ data: { promotions: [] } })),
        axios.get(`${API}/admin/marketplace/returns?status=escalated`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/events?status=pending`, { headers }).catch(() => ({ data: { pending_count: 0, change_count: 0 } })),
        axios.get(`${API}/admin/polls?status=pending`, { headers }).catch(() => ({ data: { pending_count: 0 } }))
      ]);

      // Endpoint may return {vendors,total,...} or a legacy array — normalize.
      const mktVendors = mktVendorsRes.data?.vendors || mktVendorsRes.data || [];

      setAttentionCounts({
        pending: pendingRes.data?.total ?? (Array.isArray(pendingRes.data) ? pendingRes.data.length : 0),
        reports: Array.isArray(reportsRes.data) ? reportsRes.data.filter(r => r.status === 'pending').length : 0,
        workshops: Array.isArray(workshopsRes.data) ? workshopsRes.data.filter(w => 
          w.status === 'pending_review' || w.status === 'pending_admin_approval'
        ).length : 0,
        messages: 0,
        marketplace_vendors: mktVendorsRes.data?.total ?? (Array.isArray(mktVendors) ? mktVendors.length : 0),
        spotlight_promotions: (promosRes.data?.promotions || []).length,
        return_escalations: Array.isArray(escalationsRes.data) ? escalationsRes.data.length : 0,
        // Submitted events plus changes waiting on already-approved ones — both need you.
        event_approvals: (eventsRes.data?.pending_count || 0) + (eventsRes.data?.change_count || 0)
          + (pollsRes.data?.pending_count || 0),
      });
    } catch (error) {
      console.error('Failed to fetch attention counts:', error);
    }
  };

  const fetchAllData = async () => {
    try {
      const [modulesRes, foldersRes, resourcesRes, contentsRes] = await Promise.all([
        axios.get(`${API}/modules`),
        axios.get(`${API}/content-folders`),
        axios.get(`${API}/resources`),
        axios.get(`${API}/module-contents`)
      ]);
      setModules(modulesRes.data);
      setFolders(foldersRes.data);
      setResources(resourcesRes.data);
      setContents(contentsRes.data);
      // Note: categories, events, streams are now fetched by respective tab hooks
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/all?per_page=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data?.messages || response.data || []);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = Array.isArray(response.data) ? response.data : response.data.users || [];
      setUsers(userData);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const fetchPendingUsers = async () => {
    // Note: This function exists for backward compatibility
    // AdminPendingTab has its own hook, but this is called from useEffect
    console.log('fetchPendingUsers called - AdminPendingTab manages its own state');
  };

  const fetchMessageFolders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessageFolders(response.data);
    } catch (error) {
      console.error('Failed to load message folders:', error);
      toast.error('Failed to load message folders');
    }
  };

  const fetchBackgroundMusic = async () => {
    try {
      const response = await axios.get(`${API}/background-music`);
      if (response.data.success) {
        setBackgroundMusic(response.data.music);
      }
    } catch (error) {
      console.error('Failed to load background music:', error);
    }
  };

  const fetchMusicAssignments = async () => {
    try {
      const [pageRes, moduleRes] = await Promise.all([
        axios.get(`${API}/background-music/page-assignments`),
        axios.get(`${API}/background-music/module-assignments`)
      ]);
      
      if (pageRes.data.success) {
        const pageMap = {};
        pageRes.data.assignments.forEach(a => {
          pageMap[a.page_name] = a;
        });
        setPageAssignments(pageMap);
      }
      
      if (moduleRes.data.success) {
        const moduleMap = {};
        moduleRes.data.assignments.forEach(a => {
          moduleMap[a.module_id] = a;
        });
        setModuleAssignments(moduleMap);
      }
    } catch (error) {
      console.error('Failed to load music assignments:', error);
    }
  };

  const uploadBackgroundMusic = async () => {
    if (!musicTitle.trim()) {
      toast.error('Please enter a title for the music');
      return;
    }

    if (!selectedMusicFile) {
      toast.error('Please select a music file');
      return;
    }

    setUploadingMusic(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedMusicFile);
      formData.append('title', musicTitle);

      const response = await axios.post(`${API}/background-music/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success('Music uploaded successfully!');
        setMusicTitle('');
        setSelectedMusicFile(null);
        fetchBackgroundMusic();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload music');
    } finally {
      setUploadingMusic(false);
    }
  };

  const deleteBackgroundMusic = async (musicId) => {
    if (!confirm('Are you sure you want to delete this music?')) return;

    try {
      const response = await axios.delete(`${API}/background-music/${musicId}`);
      if (response.data.success) {
        toast.success('Music deleted successfully');
        fetchBackgroundMusic();
        fetchMusicAssignments();
      }
    } catch (error) {
      toast.error('Failed to delete music');
    }
  };

  const updatePageAssignment = async (pageName, musicId, enabled = true) => {
    try {
      const response = await axios.post(`${API}/background-music/page-assignments`, {
        page_name: pageName,
        music_id: musicId,
        enabled: enabled
      });
      
      if (response.data.success) {
        toast.success('Page assignment updated');
        fetchMusicAssignments();
      }
    } catch (error) {
      toast.error('Failed to update page assignment');
    }
  };

  const updateModuleAssignment = async (moduleId, musicId, enabled = true) => {
    try {
      const response = await axios.post(`${API}/background-music/module-assignments`, {
        module_id: moduleId,
        music_id: musicId,
        enabled: enabled
      });
      
      if (response.data.success) {
        toast.success('Module assignment updated');
        fetchMusicAssignments();
      }
    } catch (error) {
      toast.error('Failed to update module assignment');
    }
  };

  // Module functions
  const resetModuleForm = () => {
    setModuleForm({ title: '', description: '', order: 1, is_locked: true, required_previous_module: '', days_delay: 0 });
    setEditingModule(null);
  };

  const handleEditModule = (module) => {
    setEditingModule(module.id);
    setModuleForm({
      title: module.title,
      description: module.description,
      order: module.order,
      is_locked: module.is_locked,
      required_previous_module: module.required_previous_module || '',
      days_delay: module.days_delay || 0
    });
    // Auto-scroll to form
    moduleFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const saveModule = async () => {
    try {
      if (editingModule) {
        await axios.put(`${API}/modules/${editingModule}`, moduleForm);
        toast.success('Module updated!');
      } else {
        await axios.post(`${API}/modules`, moduleForm);
        toast.success('Module created!');
      }
      resetModuleForm();
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save module');
    }
  };

  const deleteModule = async (id) => {
    try {
      await axios.delete(`${API}/modules/${id}`);
      toast.success('Module deleted!');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete module');
    }
  };

  // Folder functions
  const resetFolderForm = () => {
    setFolderForm({ module_id: '', name: '', description: '', order: 1, days_delay: 0 });
    setEditingFolder(null);
  };

  const handleEditFolder = (folder) => {
    setEditingFolder(folder.id);
    setFolderForm({
      module_id: folder.module_id,
      name: folder.name,
      description: folder.description,
      order: folder.order,
      days_delay: folder.days_delay || 0
    });
    // Auto-scroll to form
    folderFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const saveFolder = async () => {
    if (!folderForm.module_id || !folderForm.name) {
      toast.error('Module and folder name are required');
      return;
    }

    try {
      if (editingFolder) {
        await axios.put(`${API}/content-folders/${editingFolder}`, folderForm);
        toast.success('Folder updated successfully');
      } else {
        await axios.post(`${API}/content-folders`, folderForm);
        toast.success('Folder created successfully');
      }
      resetFolderForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save folder');
    }
  };

  const deleteFolder = async (folderId) => {
    try {
      await axios.delete(`${API}/content-folders/${folderId}`);
      toast.success('Folder and its contents deleted successfully');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete folder');
    }
  };

  // Content functions
  const resetContentForm = () => {
    setContentForm({
      module_id: '',
      folder_id: '',
      title: '',
      content_type: 'blog',
      order: 1,
      generate_ai_image: false,
      content_data: {},
      is_public: false,
      is_listed: false,
      slug: '',
      unsplash_url: ''
    });
    setEditingContent(null);
    setPhotoOptions([]);
    setSelectedPhoto(null);
  };

  const handleEditContent = (content) => {
    setEditingContent(content.id);
    setContentForm({
      module_id: content.module_id,
      folder_id: content.folder_id || '',
      title: content.title,
      content_type: content.content_type,
      order: content.order,
      generate_ai_image: false,
      content_data: content.content_data || {},
      is_public: content.is_public || false,
      is_listed: content.is_listed || false,
      slug: content.slug || ''
    });
    // Auto-scroll to form
    contentFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const createContent = async () => {
    console.log('Creating content with folder_id:', contentForm.folder_id);
    console.log('Full content form:', contentForm);
    
    // Validate folder selection
    if (!contentForm.folder_id || contentForm.folder_id === '') {
      toast.error('Please select a folder. Content must be organized in folders.');
      console.error('Folder ID is missing or empty');
      return;
    }
    
    // Handle Unsplash URL if provided
    let photoToUse = selectedPhoto;
    if (contentForm.unsplash_url && !selectedPhoto) {
      try {
        const response = await axios.post(`${API}/fetch-photo-from-url`, null, {
          params: { unsplash_url: contentForm.unsplash_url }
        });
        
        if (response.data.success) {
          photoToUse = response.data.photo;
          toast.success('Photo fetched from Unsplash!');
        } else {
          toast.error(response.data.message || 'Failed to fetch photo from URL');
          return;
        }
      } catch (error) {
        toast.error('Failed to fetch photo. Please check the URL.');
        return;
      }
    }
    
    // If photo was selected, add it to content_data with attribution
    const finalContentForm = { ...contentForm };
    if (photoToUse) {
      finalContentForm.content_data = {
        ...finalContentForm.content_data,
        ai_image: photoToUse.url,
        image_attribution: {
          photographer: photoToUse.photographer,
          photographer_url: photoToUse.photographer_url,
          unsplash_url: 'https://unsplash.com',
          description: photoToUse.description
        }
      };
      
      // Trigger Unsplash download tracking (API requirement)
      try {
        await axios.post(`${API}/trigger-photo-download`, null, {
          params: { download_location: photoToUse.download_location }
        });
      } catch (error) {
        console.error('Download tracking failed:', error);
      }
    }
    
    try {
      if (editingContent) {
        await axios.put(`${API}/module-contents/${editingContent}`, finalContentForm);
        toast.success('Content updated and assigned to folder!');
      } else {
        await axios.post(`${API}/module-contents`, finalContentForm);
        toast.success('Content created in folder!');
      }
      resetContentForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save content');
    }
  };
  
  const fetchPhotoOptions = async () => {
    if (!contentForm.title) {
      toast.error('Please enter a content title first');
      return;
    }
    
    setLoadingPhotos(true);
    try {
      const response = await axios.get(`${API}/select-photo/${encodeURIComponent(contentForm.title)}`);
      if (response.data.success && response.data.photos.length > 0) {
        setPhotoOptions(response.data.photos);
        toast.success(`Found ${response.data.photos.length} photo options!`);
      } else {
        toast.error('No photos found for this title');
        setPhotoOptions([]);
      }
    } catch (error) {
      toast.error('Failed to fetch photo options');
      setPhotoOptions([]);
    } finally {
      setLoadingPhotos(false);
    }
  };
  
  const searchUnsplash = async (query) => {
    if (!query) return;
    
    setLoadingPhotos(true);
    try {
      const response = await axios.get(`${API}/select-photo/${encodeURIComponent(query)}`);
      if (response.data.success && response.data.photos.length > 0) {
        setUnsplashResults(response.data.photos);
      } else {
        setUnsplashResults([]);
        toast.error('No photos found');
      }
    } catch (error) {
      toast.error('Failed to search Unsplash');
      setUnsplashResults([]);
    } finally {
      setLoadingPhotos(false);
    }
  };
  
  const openUnsplashBrowser = () => {
    setUnsplashSearchQuery(contentForm.title || 'spiritual meditation');
    setShowUnsplashModal(true);
    searchUnsplash(contentForm.title || 'spiritual meditation');
  };
  
  const selectPhotoFromModal = (photo) => {
    setSelectedPhoto(photo);
    setShowUnsplashModal(false);
    toast.success('Photo selected!');
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    
    try {
      await axios.delete(`${API}/module-contents/${contentId}`);
      toast.success('Content deleted!');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const toggleModuleExpansion = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const getContentsByModule = (moduleId) => {
    return contents.filter(c => c.module_id === moduleId).sort((a, b) => a.order - b.order);
  };

  // Category functions
  const resetCategoryForm = () => {
    setCategoryForm({ name: '', description: '', allow_homework: false });
    setEditingCategory(null);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category.id);
    setCategoryForm({
      name: category.name,
      description: category.description,
      allow_homework: category.allow_homework || false
    });
    // Auto-scroll to form
    categoryFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const saveCategory = async () => {
    try {
      if (editingCategory) {
        await axios.put(`${API}/forum/categories/${editingCategory}`, categoryForm);
        toast.success('Category updated!');
      } else {
        await axios.post(`${API}/forum/categories`, categoryForm);
        toast.success('Category created!');
      }
      resetCategoryForm();
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const deleteCategory = async (id) => {
    try {
      await axios.delete(`${API}/forum/categories/${id}`);
      toast.success('Category deleted!');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // Event functions
  const resetEventForm = () => {
    setEventForm({ title: '', description: '', event_date: '', event_time: '', link: '' });
    setEditingEvent(null);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event.id);
    setEventForm({
      title: event.title,
      description: event.description,
      event_date: event.event_date,
      event_time: event.event_time,
      link: event.link || ''
    });
    // Auto-scroll to form
    eventFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const saveEvent = async () => {
    try {
      if (editingEvent) {
        await axios.put(`${API}/calendar/events/${editingEvent}`, eventForm);
        toast.success('Event updated!');
      } else {
        await axios.post(`${API}/calendar/events`, eventForm);
        toast.success('Event created!');
      }
      resetEventForm();
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save event');
    }
  };

  const deleteEvent = async (id) => {
    try {
      await axios.delete(`${API}/calendar/events/${id}`);
      toast.success('Event deleted!');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // Resource functions
  const resetResourceForm = () => {
    setResourceForm({ 
      name: '', 
      title: '', 
      phone: '',
      bio: '', 
      website: '', 
      photo_url: '',
      facebook: '',
      instagram: '',
      linkedin: '',
      twitter: '',
      youtube: '',
      tiktok: '',
      spotify: '',
      email: '',
      custom_link_1_label: '',
      custom_link_1_url: '',
      custom_link_2_label: '',
      custom_link_2_url: '',
      custom_link_3_label: '',
      custom_link_3_url: ''
    });
    setEditingResource(null);
  };

  const handleEditResource = (resource) => {
    setEditingResource(resource.id);
    setResourceForm({
      name: resource.name,
      title: resource.title,
      phone: resource.phone || '',
      bio: resource.bio,
      website: resource.website || '',
      photo_url: resource.photo_url || '',
      facebook: resource.facebook || '',
      instagram: resource.instagram || '',
      linkedin: resource.linkedin || '',
      twitter: resource.twitter || '',
      youtube: resource.youtube || '',
      tiktok: resource.tiktok || '',
      spotify: resource.spotify || '',
      email: resource.email || '',
      custom_link_1_label: resource.custom_link_1_label || '',
      custom_link_1_url: resource.custom_link_1_url || '',
      custom_link_2_label: resource.custom_link_2_label || '',
      custom_link_2_url: resource.custom_link_2_url || '',
      custom_link_3_label: resource.custom_link_3_label || '',
      custom_link_3_url: resource.custom_link_3_url || ''
    });
    // Auto-scroll to form
    resourceFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const saveResource = async () => {
    try {
      if (editingResource) {
        await axios.put(`${API}/resources/${editingResource}`, resourceForm);
        toast.success('Resource updated!');
      } else {
        await axios.post(`${API}/resources`, resourceForm);
        toast.success('Resource created!');
      }
      resetResourceForm();
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save resource');
    }
  };

  const handleDragStart = (e, resource) => {
    setDraggedResource(resource);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetResource) => {
    e.preventDefault();
    
    if (!draggedResource || draggedResource.id === targetResource.id) {
      setDraggedResource(null);
      return;
    }

    // Reorder resources locally
    const draggedIndex = resources.findIndex(r => r.id === draggedResource.id);
    const targetIndex = resources.findIndex(r => r.id === targetResource.id);
    
    const newResources = [...resources];
    newResources.splice(draggedIndex, 1);
    newResources.splice(targetIndex, 0, draggedResource);
    
    setResources(newResources);
    
    // Send new order to backend
    const resourceIds = newResources.map(r => r.id);
    try {
      await axios.put(`${API}/resources/reorder`, { resource_ids: resourceIds });
      toast.success('Resources reordered!');
    } catch (error) {
      console.error('Reorder error:', error);
      console.error('Error response:', error.response?.data);
      
      // Handle error message properly
      let errorMessage = 'Failed to save new order';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg).join(', ');
        }
      }
      
      toast.error(errorMessage);
      fetchAllData(); // Revert on error
    }
    
    setDraggedResource(null);
  };

  const deleteResource = async (id) => {
    try {
      await axios.delete(`${API}/resources/${id}`);
      toast.success('Resource deleted!');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  // Stream functions
  const resetStreamForm = () => {
    setStreamForm({ 
      title: '', 
      description: '', 
      embed_url: '', 
      scheduled_date: '', 
      timezone: 'America/Chicago', 
      is_live: false, 
      add_to_calendar: false,
      is_paid: false,
      live_price: 0,
      replay_price: 0,
      stream_password: ''
    });
    setEditingStream(null);
  };

  const handleEditStream = (stream) => {
    setEditingStream(stream.id);
    setStreamForm({
      title: stream.title,
      description: stream.description,
      embed_url: stream.embed_url,
      scheduled_date: stream.scheduled_date || '',
      timezone: stream.timezone || 'America/Chicago',
      is_live: stream.is_live,
      add_to_calendar: stream.add_to_calendar || false,
      is_paid: stream.is_paid || false,
      live_price: stream.live_price || 0,
      replay_price: stream.replay_price || 0,
      stream_password: stream.stream_password || ''
    });
    // Auto-scroll to form
    streamFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const saveStream = async () => {
    try {
      if (editingStream) {
        await axios.put(`${API}/live-streams/${editingStream}`, streamForm);
        toast.success('Stream updated!');
      } else {
        await axios.post(`${API}/live-streams`, streamForm);
        toast.success('Stream created!');
      }
      resetStreamForm();
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save stream');
    }
  };

  const deleteStream = async (id) => {
    try {
      await axios.delete(`${API}/live-streams/${id}`);
      toast.success('Stream deleted!');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete stream');
    }
  };

  // Message functions
  const resetMessageForm = () => {
    setMessageForm({
      recipient_ids: [],
      subject: '',
      message: '',
      priority: 'normal',
      send_notification: true
    });
    setSelectedUsers([]);
    setAdminSelectedFiles([]);
  };

  const handleSendMessage = async () => {
    try {
      if (selectedUsers.length === 0) {
        toast.error('Please select at least one recipient');
        return;
      }
      if (!messageForm.subject.trim() || !messageForm.message.trim()) {
        toast.error('Please fill in subject and message');
        return;
      }

      const token = localStorage.getItem('token');
      const payload = {
        ...messageForm,
        recipient_ids: selectedUsers
      };
      
      const response = await axios.post(`${API}/messages/send`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Upload files to all created messages if any files were selected
      if (adminSelectedFiles.length > 0 && response.data.message_ids) {
        const formData = new FormData();
        adminSelectedFiles.forEach(file => {
          formData.append('files', file);
        });

        // Upload to each created message
        for (const messageId of response.data.message_ids) {
          try {
            await axios.post(
              `${API}/messages/${messageId}/attachments`,
              formData,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
          } catch (uploadError) {
            console.error(`Failed to upload attachments to message ${messageId}:`, uploadError);
          }
        }
      }
      
      toast.success(response.data.message || 'Message sent successfully!');
      resetMessageForm();
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleReplyToMessage = async (messageId, reply) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/messages/${messageId}/reply`, 
        { admin_reply: reply },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reply sent successfully!');
      setReplyingTo(null); // Close the reply form
      setReplyText(''); // Clear reply text
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const handleResolveMessage = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/messages/${messageId}/resolve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message marked as resolved');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to resolve message');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message deleted successfully');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    setSelectedUsers(users.map(u => u.id));
  };

  const deselectAllUsers = () => {
    setSelectedUsers([]);
  };

  const handleReactToMessage = async (messageId, emoji) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/messages/${messageId}/react`,
        { emoji },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMessages(); // Refresh to show updated reactions
    } catch (error) {
      toast.error('Failed to add reaction');
    }
  };

  // Message folder management functions
  const resetMessageFolderForm = () => {
    setMessageFolderForm({ name: '', description: '', color: '#808080' });
    setEditingMessageFolder(null);
  };

  const handleCreateMessageFolder = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/messages/folders`, messageFolderForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Folder created successfully!');
      resetMessageFolderForm();
      fetchMessageFolders();
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  const handleUpdateMessageFolder = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/messages/folders/${editingMessageFolder}`, messageFolderForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Folder updated successfully!');
      resetMessageFolderForm();
      fetchMessageFolders();
    } catch (error) {
      toast.error('Failed to update folder');
    }
  };

  const handleDeleteMessageFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Messages in this folder will become unassigned.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/messages/folders/${folderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Folder deleted successfully!');
      fetchMessageFolders();
      fetchMessages();
    } catch (error) {
      toast.error('Failed to delete folder');
    }
  };

  const handleEditMessageFolder = (folder) => {
    setEditingMessageFolder(folder.id);
    setMessageFolderForm({
      name: folder.name,
      description: folder.description || '',
      color: folder.color || '#808080'
    });
    // Auto-scroll to form
    messageFolderFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Bulk message operations
  const toggleMessageSelection = (messageId) => {
    setSelectedMessages(prev =>
      prev.includes(messageId)
        ? prev.filter(id => id !== messageId)
        : [...prev, messageId]
    );
  };

  const selectAllMessages = () => {
    const visibleMessageIds = getFilteredMessages().map(m => m.id);
    setSelectedMessages(visibleMessageIds);
  };

  const deselectAllMessages = () => {
    setSelectedMessages([]);
  };

  const handleBulkDelete = async () => {
    if (selectedMessages.length === 0) {
      toast.error('No messages selected');
      return;
    }

    if (!window.confirm(`Delete ${selectedMessages.length} message(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/messages/bulk-delete`, 
        { message_ids: selectedMessages },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Successfully deleted ${selectedMessages.length} message(s)`);
      setSelectedMessages([]);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to delete messages');
    }
  };

  const handleMoveToFolder = async (messageId, folderId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/messages/${messageId}/folder`,
        { folder_id: folderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Message moved successfully!');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to move message');
    }
  };

  const getFilteredMessages = () => {
    if (selectedFolder === null) {
      return messages; // Show all messages
    }
    if (selectedFolder === 'unassigned') {
      return messages.filter(m => !m.folder_id);
    }
    return messages.filter(m => m.folder_id === selectedFolder);
  };

  return (
    <div className="w-full max-w-6xl mx-auto overflow-x-hidden">
      <div className="mb-6 sm:mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-2 sm:mb-3 break-words">Admin Panel</h1>
          <p className="text-sm sm:text-base lg:text-lg text-gray-300">Manage all platform content</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowTabOrder(true)}
          className="text-gray-300 hover:text-white hover:bg-white/10 border border-white/10"
          data-testid="admin-reorder-tabs-button"
        >
          <ArrowUpDown className="w-4 h-4 mr-2" />
          Reorder tabs
        </Button>
      </div>

      <TabOrderDialog
        open={showTabOrder}
        onOpenChange={setShowTabOrder}
        savedOrder={tabOrder}
        ready={tabOrderLoaded}
        onSaved={setTabOrder}
      />

      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          setSearchParams(v === 'modules' ? {} : { tab: v }, { replace: true });
          fetchAttentionCounts();
        }}
        className="space-y-4 sm:space-y-6 w-full"
      >
        {/* Categorized Tab Navigation — order comes from Admin → Reorder tabs */}
        <div className="w-full space-y-2" data-testid="admin-tab-categories">
          {arrangedCategories.map((category) => (
            <div key={category.key}>
              <CategoryHeader
                label={category.label}
                icon={category.icon}
                isExpanded={expandedCategories[category.key]}
                onToggle={() => toggleCategory(category.key)}
                color={category.color}
              />
              {expandedCategories[category.key] && (
                <TabsList className="flex flex-wrap h-auto gap-1 p-1 mt-1 justify-start ml-2">
                  {category.tabs.map((tab) => (
                    <AdminTabTrigger
                      key={tab.value}
                      value={tab.value}
                      label={tab.label}
                      attentionCount={tab.attentionKey ? attentionCounts[tab.attentionKey] : undefined}
                    />
                  ))}
                </TabsList>
              )}
            </div>
          ))}
        </div>

        {/* Modules Tab */}
        <TabsContent value="modules" className="space-y-6">
          <AdminModulesTab />
        </TabsContent>

        {/* Folders Tab */}
        <TabsContent value="folders">
          <AdminFoldersTab modules={modules} />
        </TabsContent>

        <TabsContent value="pacing">
          <AdminPacingTab />
        </TabsContent>

        <TabsContent value="practitioners">
          <AdminPractitionersTab />
        </TabsContent>

        <TabsContent value="vendor-invites">
          <AdminVendorInvitesTab />
        </TabsContent>

        <TabsContent value="onboarding">
          <AdminOnboardingTab />
        </TabsContent>

        {/* Content Tab */}
        {/* Content Tab */}
        <TabsContent value="assessment-builder" data-testid="assessment-builder-tab-content">
          <AdminAssessmentBuilderTab />
        </TabsContent>

        <TabsContent value="assessment-scoring" data-testid="assessment-scoring-tab-content">
          <AdminAssessmentScoringTab />
        </TabsContent>

        <TabsContent value="content">
          <AdminContentTab
            contents={contents}
            contentForm={contentForm}
            setContentForm={setContentForm}
            editingContent={editingContent}
            contentFormRef={contentFormRef}
            modules={modules}
            folders={folders}
            photoOptions={photoOptions}
            selectedPhoto={selectedPhoto}
            setSelectedPhoto={setSelectedPhoto}
            loadingPhotos={loadingPhotos}
            showUnsplashModal={showUnsplashModal}
            setShowUnsplashModal={setShowUnsplashModal}
            unsplashSearchQuery={unsplashSearchQuery}
            setUnsplashSearchQuery={setUnsplashSearchQuery}
            unsplashResults={unsplashResults}
            expandedModules={expandedModules}
            setExpandedModules={setExpandedModules}
            expandedFolders={expandedFolders}
            setExpandedFolders={setExpandedFolders}
            viewPhotoModal={viewPhotoModal}
            setViewPhotoModal={setViewPhotoModal}
            selectedPhotoUrl={selectedPhotoUrl}
            setSelectedPhotoUrl={setSelectedPhotoUrl}
            selectedPhotoAttribution={selectedPhotoAttribution}
            setSelectedPhotoAttribution={setSelectedPhotoAttribution}
            createContent={createContent}
            fetchPhotoOptions={fetchPhotoOptions}
            searchUnsplash={searchUnsplash}
            openUnsplashBrowser={openUnsplashBrowser}
            selectPhotoFromModal={selectPhotoFromModal}
            handleDeleteContent={handleDeleteContent}
            handleEditContent={handleEditContent}
            resetContentForm={resetContentForm}
            toggleModuleExpansion={toggleModuleExpansion}
          />
        </TabsContent>


        {/* Forum Tab */}
        <TabsContent value="forum" className="space-y-6">
          <AdminForumTab />
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          <AdminReportsTab ReportsManagerComponent={ReportsManager} />
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-6">
          <AdminCalendarTab />
        </TabsContent>

        {/* Practitioner-submitted events awaiting review */}
        <TabsContent value="event-approvals" className="space-y-6">
          <AdminEventApprovalsTab />
        </TabsContent>

        {/* Guides Tab */}
        {/* Guides Tab */}
        <TabsContent value="resources" className="space-y-6">
          <AdminGuidesTab
            resources={resources}
            resourceForm={resourceForm}
            setResourceForm={setResourceForm}
            editingResource={editingResource}
            resetResourceForm={resetResourceForm}
            saveResource={saveResource}
            deleteResource={deleteResource}
            handleEditResource={handleEditResource}
            handleDragStart={handleDragStart}
            handleDragOver={handleDragOver}
            handleDrop={handleDrop}
            draggedResource={draggedResource}
            resourceFormRef={resourceFormRef}
            onPlacardsChanged={fetchAllData}
          />
        </TabsContent>


        {/* Streams Tab */}
        <TabsContent value="streams" className="space-y-6">
          <AdminStreamsTab />
        </TabsContent>

        <TabsContent value="promotions" className="space-y-6" data-testid="promotions-tab-content">
          <AdminPromotionsTab />
        </TabsContent>
        {/* Background Music Tab */}
        {/* Background Music Tab */}
        <TabsContent value="music" className="space-y-6">
          <AdminMusicTab
            musicFormRef={musicFormRef}
            musicTitle={musicTitle}
            setMusicTitle={setMusicTitle}
            selectedMusicFile={selectedMusicFile}
            setSelectedMusicFile={setSelectedMusicFile}
            uploadingMusic={uploadingMusic}
            uploadBackgroundMusic={uploadBackgroundMusic}
            backgroundMusic={backgroundMusic}
            deleteBackgroundMusic={deleteBackgroundMusic}
            pageAssignments={pageAssignments}
            updatePageAssignment={updatePageAssignment}
            modules={modules}
            moduleAssignments={moduleAssignments}
            updateModuleAssignment={updateModuleAssignment}
          />
        </TabsContent>

        <TabsContent value="portal-videos" className="space-y-6">
          <AdminPortalVideoTab />
        </TabsContent>


        {/* Members Tab */}
        <TabsContent value="members" className="space-y-6">
          <AdminMembershipManagement user={user} />
        </TabsContent>

        {/* Blocked Members Tab */}
        <TabsContent value="blocked" className="space-y-6">
          <AdminBlockedMembersTab />
        </TabsContent>

        {/* Contributors Tab */}
        <TabsContent value="contributors" className="space-y-6">
          <AdminContributorsTab />
        </TabsContent>

        {/* Pending Users Tab */}
        {/* Pending Users Tab */}
        <TabsContent value="pending" className="space-y-6">
          <AdminPendingTab />
        </TabsContent>
        {/* Legal Documents Tab */}
        <TabsContent value="legal" className="space-y-6">
          <AdminLegalDocuments user={user} />
        </TabsContent>

        {/* Pricing Tab (Legacy) */}
        <TabsContent value="pricing" className="space-y-6">
          <AdminPricingSettings />
        </TabsContent>

        {/* Tier Pricing Tab (NEW) */}
        <TabsContent value="tier-pricing" className="space-y-6">
          <AdminTierPricing />
        </TabsContent>

        {/* Subscription Pricing Tab (NEW) */}
        <TabsContent value="subscription-pricing" className="space-y-6">
          <AdminSubscriptionPricing />
        </TabsContent>

        {/* Payouts Management Tab */}
        <TabsContent value="payouts" className="space-y-6">
          <AdminPayoutsTab />
        </TabsContent>

        {/* Sales Tax (Stripe Tax) Tab */}
        <TabsContent value="sales-tax" className="space-y-6" data-testid="sales-tax-tab-content">
          <AdminTaxTab />
        </TabsContent>

        {/* Stripe wiring / health */}
        <TabsContent value="stripe" className="space-y-6" data-testid="stripe-tab-content">
          <AdminStripeTab />
        </TabsContent>

        {/* Launch readiness / pre-deployment checklist */}
        <TabsContent value="launch" className="space-y-6" data-testid="launch-tab-content">
          <AdminLaunchTab />
        </TabsContent>

        {/* Tutorials (Help Videos) Tab */}
        <TabsContent value="tutorials" className="space-y-6">
          <AdminHelpVideosTab />
        </TabsContent>

        {/* Spotlights (Advertising) Tab */}
        <TabsContent value="spotlights" className="space-y-6">
          <AdminSpotlightsTab />
        </TabsContent>

        {/* Marketplace Shelves Tab */}
        <TabsContent value="shelves" className="space-y-6" data-testid="shelves-tab-content">
          <AdminShelvesTab />
        </TabsContent>

        {/* Marketplace Inventory Tab */}
        <TabsContent value="mkt-inventory" className="space-y-6" data-testid="mkt-inventory-tab-content">
          <AdminInventoryTab />
        </TabsContent>

        {/* Q&A Tab */}
        <TabsContent value="qa" className="space-y-6" data-testid="qa-tab-content">
          <AdminQATab />
        </TabsContent>

        {/* Worksheets Tab */}
        <TabsContent value="worksheets" className="space-y-6" data-testid="worksheets-tab-content">
          <AdminWorksheetsTab />
        </TabsContent>

        {/* Ritual Cards Tab — the Sacred Habit Stack library */}
        <TabsContent value="ritual-cards" className="space-y-6" data-testid="ritual-cards-tab-content">
          <AdminRitualCardsTab />
        </TabsContent>

        {/* Email Management Tab */}
        <TabsContent value="emails" className="space-y-6">
          <AdminEmailsTab />
        </TabsContent>

        <TabsContent value="account-access" className="space-y-6" data-testid="account-access-tab-content">
          <AdminAccountAccess />
        </TabsContent>

        {/* Messages Tab */}
        {/* Messages Tab */}
        <TabsContent value="messages" className="space-y-6">
          <AdminMessagesTab
            user={user}
            messageFolderFormRef={messageFolderFormRef}
            messageFolders={messageFolders}
            fetchMessageFolders={fetchMessageFolders}
            messageFolderForm={messageFolderForm}
            setMessageFolderForm={setMessageFolderForm}
            editingMessageFolder={editingMessageFolder}
            handleCreateMessageFolder={handleCreateMessageFolder}
            handleUpdateMessageFolder={handleUpdateMessageFolder}
            handleDeleteMessageFolder={handleDeleteMessageFolder}
            handleEditMessageFolder={handleEditMessageFolder}
            resetMessageFolderForm={resetMessageFolderForm}
            messageForm={messageForm}
            setMessageForm={setMessageForm}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            toggleUserSelection={toggleUserSelection}
            selectAllUsers={selectAllUsers}
            deselectAllUsers={deselectAllUsers}
            adminSelectedFiles={adminSelectedFiles}
            setAdminSelectedFiles={setAdminSelectedFiles}
            users={users}
            fetchUsers={fetchUsers}
            handleSendMessage={handleSendMessage}
            resetMessageForm={resetMessageForm}
            selectedFolder={selectedFolder}
            setSelectedFolder={setSelectedFolder}
            messages={messages}
            fetchMessages={fetchMessages}
            selectedMessages={selectedMessages}
            toggleMessageSelection={toggleMessageSelection}
            selectAllMessages={selectAllMessages}
            deselectAllMessages={deselectAllMessages}
            handleBulkDelete={handleBulkDelete}
            handleMoveToFolder={handleMoveToFolder}
            replyingTo={replyingTo}
            setReplyingTo={setReplyingTo}
            replyText={replyText}
            setReplyText={setReplyText}
            handleReplyToMessage={handleReplyToMessage}
            handleResolveMessage={handleResolveMessage}
            handleDeleteMessage={handleDeleteMessage}
            handleReactToMessage={handleReactToMessage}
          />
        </TabsContent>


        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <AdminAnalytics />
        </TabsContent>

        {/* Event Cleanup Tab */}
        <TabsContent value="event-cleanup" className="space-y-6">
          <AdminEventCleanup />
        </TabsContent>
        
        {/* Workshops Tab - Admin workshop management */}
        <TabsContent value="workshops" className="space-y-6">
          <AdminWorkshopsTab 
            onPendingCountChange={(count) => setAttentionCounts(prev => ({ ...prev, workshops: count }))}
          />
        </TabsContent>
        
        {/* Workshop Analytics Tab - Sales and payouts */}
        <TabsContent value="workshop-analytics" className="space-y-6">
          <AdminWorkshopAnalyticsTab />
        </TabsContent>
        
        {/* Marketplace Analytics Tab */}
        <TabsContent value="mkt-analytics" className="space-y-6" data-testid="mkt-analytics-tab-content">
          <MarketplaceAdminDashboard />
        </TabsContent>
        
        {/* Marketplace Vendor Approvals Tab */}
        <TabsContent value="mkt-vendors" className="space-y-6" data-testid="mkt-vendors-tab-content">
          <MarketplaceVendorApprovals />
        </TabsContent>

        {/* Marketplace Orders Tab (parent payment + per-vendor receipts) */}
        <TabsContent value="mkt-orders" className="space-y-6" data-testid="mkt-orders-tab-content">
          <AdminMarketplaceOrdersTab />
        </TabsContent>

        {/* Marketplace held payouts */}
        <TabsContent value="mkt-payouts" className="space-y-6" data-testid="mkt-payouts-tab-content">
          <AdminHeldPayoutsTab />
        </TabsContent>
      </Tabs>
      
      {/* Unsplash Photo Browser Modal */}
      {showUnsplashModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-xl font-semibold">Browse Unsplash Photos</h3>
              <Button
                onClick={() => setShowUnsplashModal(false)}
                variant="ghost"
                size="sm"
              >
                ✕
              </Button>
            </div>
            
            {/* Search Bar */}
            <div className="p-4 border-b">
              <div className="flex gap-2">
                <Input
                  value={unsplashSearchQuery}
                  onChange={(e) => setUnsplashSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchUnsplash(unsplashSearchQuery)}
                  placeholder="Search for photos..."
                  className="flex-1"
                />
                <Button
                  onClick={() => searchUnsplash(unsplashSearchQuery)}
                  disabled={loadingPhotos}
                >
                  {loadingPhotos ? 'Searching...' : 'Search'}
                </Button>
              </div>
            </div>
            
            {/* Photo Results */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingPhotos ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">Loading photos...</p>
                </div>
              ) : unsplashResults.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {unsplashResults.map((photo) => (
                    <div
                      key={photo.id}
                      onClick={() => selectPhotoFromModal(photo)}
                      className="cursor-pointer border-2 border-gray-200 rounded-lg overflow-hidden hover:border-cyan-500 transition-all hover:shadow-lg"
                    >
                      <img
                        src={photo.regular}
                        alt={photo.description || 'Unsplash photo'}
                        className="w-full h-48 object-cover"
                      />
                      <div className="p-3 bg-gray-50">
                        <p className="text-sm text-gray-700 mb-1">
                          by <span className="font-medium">{photo.photographer}</span>
                        </p>
                        {photo.description && (
                          <p className="text-xs text-gray-500 line-clamp-2">
                            {photo.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">No photos found. Try a different search term.</p>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t bg-gray-50 text-center">
              <p className="text-xs text-gray-600">
                Photos provided by <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" className="underline">Unsplash</a>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Photo Preview Modal */}
      {viewPhotoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setViewPhotoModal(false)}>
          <div className="bg-white rounded-lg max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">Photo Preview</h3>
              <button
                onClick={() => setViewPhotoModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-4">
              {selectedPhotoUrl && (
                <>
                  <img 
                    src={selectedPhotoUrl} 
                    alt="Content photo preview" 
                    className="w-full rounded-lg shadow-lg"
                  />
                  {selectedPhotoAttribution && (
                    <p className="text-xs text-gray-500 mt-3 text-right">
                      Photo by{' '}
                      <a 
                        href={selectedPhotoAttribution.photographer_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-600 hover:underline"
                      >
                        {selectedPhotoAttribution.photographer}
                      </a>
                      {' '}on{' '}
                      <a 
                        href="https://unsplash.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-600 hover:underline"
                      >
                        Unsplash
                      </a>
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
